import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Select, { createFilter } from "react-select";
import _ from "lodash";

/* 
======================== Multi ===========================
Multi Select has been disabled.
This is due to the backend servernot being able to handle receiving multiple options.
This may be re-enabled when the server is able to handle it.
 =========================================================
*/

function FuseSelectInput(props) {
  const dropKeyColumn = props?.dropKeyColumn || "key";
  const dropValueColumn = props?.dropValueColumn || "value";
  const _options = props?.dropDownValues || [];

  const _isSelected = (val, testKey) => (testKey + "")?.toUpperCase() === (val?.[dropKeyColumn] + "").toUpperCase();

  const inputComponent = useRef(null);
  const [displayLabel, setDisplayLabel] = useState("");

  const [isValidValue, setIsValidValue] = useState(false);

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: "start"
  };

  const fuseReactSelectTheme = (base) => {
    const theme = { ...base };
    if (!isValidValue) theme.colors.primary = "var(--danger)";
    else theme.colors.primary = "var(--primary)";
    return theme;
  };

  const fuseReactSelectCustomStyles = {
    control: (base, state) => {
      const style = {
        ...base,
        minHeight: "30px"
      };
      if (!isValidValue) {
        style["borderColor"] = "var(--danger)";
        style[":hover"] = { borderColor: "var(--danger)" };
      }
      return style;
    },
    container: (base) => {
      const style = {
        ...base,
        margin: 0,
        padding: 0
      };
      return style;
    },
    indicatorSeparator: (base) => {
      const style = { ...base };
      if (!isValidValue) {
        style["backgroundColor"] = "var(--danger)";
        style[":hover"] = { backgroundColor: "var(--danger)" };
      }
      return style;
    },
    dropdownIndicator: (base) => {
      const style = {
        ...base,
        margin: 0,
        padding: "0px 10px"
      };
      if (!isValidValue) {
        style["color"] = "var(--danger)";
        style[":hover"] = { color: "var(--danger)" };
      }
      return style;
    },
    clearIndicator: (base) => ({
      ...base,
      margin: 0,
      padding: "0px 2px"
    }),
    menuList: (base) => ({
      ...base,
      margin: 0,
      padding: "0px"
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 })
  };

  //CommponentDidMount
  useEffect(() => {
    updateValidity();
  });

  useEffect(() => {
    let initSelectedOptions = null;
    if (props?.value != null && props?.value !== "") {
      const opts = _options.filter((opt) => _isSelected(opt, props?.value));
      if (opts.length > 0) initSelectedOptions = opts[0];
    }
    setDisplayLabel(initSelectedOptions?.[dropValueColumn] || "");
  }, [props.value, _options]);

  const getErrorDescription = (err) => {
    if (err === "ValueMissing") return `This field is required. Please select an option.`;
    return `This field value is not valid`;
  };

  const _invalidErrorMessage = () => {
    let errors = getValidateErrors();
    if (errors.length > 0) return getErrorDescription(errors[0]);
    else return "";
  };

  const checkValid = () => getValidateErrors().length === 0;
  const getValidateErrors = () => {
    let errors = [];
    if (inputComponent?.current != null) {
      const { validity } = inputComponent.current;
      if (validity?.valueMissing === true) errors.push("ValueMissing");
      if (validity?.tooShort === true) errors.push("TooShort");
      if (validity?.tooLong === true) errors.push("TooLong");
      if (validity?.patternMismatch == true) errors.push("PatternMismatch");
      if (validity?.badInput === true) errors.push("BadInput");
      if (validity?.rangeOverflow === true) errors.push("RangeOverflow");
      if (validity?.rangeUnderflow === true) errors.push("RangeUnderflow");
      if (validity?.stepMismatch === true) errors.push("StepMismatch");
      if (validity?.typeMismatch === true) errors.push("TypeMismatch");
      // console.log(props.id, validity, errors);
    } // else if (props?.id) console.log("current == null ", props.id);
    return errors;
  };

  const updateValidity = () => {
    if (!inputComponent?.current) return;
    setIsValidValue(checkValid());
    inputComponent.current.setCustomValidity(_invalidErrorMessage());
  };

  const handleChanges = (selected) => {
    // console.log(selected);

    props?.onChange(selected?.value || "");
  };

  const _getValue = () => {
    if (props?.value != null && props?.value !== "" && displayLabel !== "")
      return {
        //internal <Select> expected property names
        value: props?.value,
        label: displayLabel,
        selected: true
      };
    else return null;
  };

  const _getOptions = () => {
    return _options.map((opt) => ({
      value: opt[dropKeyColumn],
      label: opt[dropValueColumn]
    }));
  };

  if (props.editable) {
    return (
      <div className="col-sm-12" key={props?.id}>
        <Select
          //type
          menuPortalTarget={document.body}
          // className="col-sm-12"
          //identifiers
          id={props.id + "-select"}
          //values
          value={_getValue()}
          options={_getOptions()}
          isOptionSelected={(opt) => _isSelected(opt, props?.value)}
          placeholder={props.placeholder}
          styles={fuseReactSelectCustomStyles}
          theme={fuseReactSelectTheme}
          //settings
          isDisabled={props.disabled || false}
          isClearable={true}
          isSearchable={true}
          isMulti={false}
          filterOption={createFilter(filterConfig)}
          //handlers
          onChange={handleChanges}
        />
        <input
          //type
          ref={inputComponent}
          type=""
          //identifiers
          id={props.id}
          //values
          value={displayLabel || ""}
          //settings
          tabIndex={-1}
          autoComplete="off"
          onChange={() => {}}
          style={{
            opacity: 0,
            height: 0,
            // width: "100%",
            position: "absolute",
            top: "70%"
            // left: "0",
          }}
          //validation
          required={props.required}
        />
      </div>
    );
  } else {
    return <div key={props?.id}>{displayLabel || ""}</div>;
  }
}

FuseSelectInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  id: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  isMulti: PropTypes.bool,
  // isClearable: PropTypes.bool,
  dropDownValues: PropTypes.array,
  dropDownDisplayValues: PropTypes.array,
  dropKeyColumn: PropTypes.string.isRequired,
  dropValueColumn: PropTypes.string.isRequired
};

export default FuseSelectInput;
